'use client';

import React from 'react';

/**
 * Sparse line-art pharmacy figures behind the hero.
 *
 * Replaces the dot grid, which read as generic texture rather than as this
 * shop. These are drawn thin and near-transparent and parked at the edges of
 * the band — the hero copy sits in the middle, so nothing lands behind text.
 *
 * Rendered inside the page's absolute z-[1] backdrop layer: every section
 * paints an opaque white background, so a figure placed *behind* them would
 * never be seen. z-[1] puts it above those backgrounds but below the hero's
 * own z-10 content.
 *
 * Each figure drifts on its own slow cycle. The `motif-drift` keyframes live
 * here and are frozen wholesale by the reduced-motion block.
 */
const STROKE = 'rgba(17,24,39,0.13)';
const ACCENT = 'rgb(var(--brand)/0.30)';

const BackdropMotifs = () => (
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    <style>{`
      @keyframes motif-drift {
        0%, 100% { transform: translate3d(0, 0, 0) rotate(var(--tilt, 0deg)); }
        50%      { transform: translate3d(0, -18px, 0) rotate(calc(var(--tilt, 0deg) + 3deg)); }
      }
      .motif { position: absolute; will-change: transform; }
      @media (prefers-reduced-motion: reduce) {
        .motif { animation: none !important; }
      }
    `}</style>

    {/* Pharmacy cross — top right */}
    <div
      className="motif"
      style={{ top: '9%', right: '4%', '--tilt': '10deg', animation: 'motif-drift 16s ease-in-out infinite 1.2s' }}
    >
      <svg width="86" height="86" viewBox="0 0 80 80" fill="none">
        <path
          d="M32 8h16v16h16v16H48v16H32V40H16V24h16V8Z"
          stroke={STROKE}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      </svg>
    </div>

    {/* Molecule ring — mid right */}
    <div
      className="motif hidden lg:block"
      style={{ top: '48%', right: '7%', '--tilt': '0deg', animation: 'motif-drift 19s ease-in-out infinite 0.6s' }}
    >
      <svg width="122" height="122" viewBox="0 0 120 120" fill="none">
        <polygon points="72,40 60,62 36,62 24,40 36,18 60,18" stroke={STROKE} strokeWidth="1.5" fill="none" />
        <line x1="72" y1="40" x2="100" y2="28" stroke={STROKE} strokeWidth="1.5" />
        <line x1="60" y1="62" x2="68" y2="92" stroke={STROKE} strokeWidth="1.5" />
        <circle cx="100" cy="28" r="4" fill={ACCENT} />
        <circle cx="68" cy="92" r="3.5" stroke={STROKE} strokeWidth="1.5" fill="none" />
      </svg>
    </div>

    {/* Leaf — lower right */}
    <div
      className="motif hidden md:block"
      style={{ top: '76%', right: '14%', '--tilt': '-12deg', animation: 'motif-drift 17s ease-in-out infinite 1.6s' }}
    >
      <svg width="74" height="74" viewBox="0 0 72 72" fill="none">
        <path d="M12 60C12 30 32 12 60 12c0 28-18 48-48 48Z" stroke={STROKE} strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M12 60C24 46 38 32 58 16" stroke={STROKE} strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    </div>
  </div>
);

export default React.memo(BackdropMotifs);
